// src/components/work/WorkCalendar.jsx
// Production-grade FullCalendar wrapper (SSOT-friendly) with DEBUG logs
// ──────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiRepeat, FiPaperclip } from 'react-icons/fi';

const VIEW_OPTS = ['month', 'week', 'day'];

// ───────────────────────── DEBUG helper ─────────────────────────
const CAL_DEBUG = String(process.env.REACT_APP_CALENDAR_DEBUG || '').toLowerCase() === 'true';
const calLog = (...args) => {
  if (CAL_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[WorkCalendar]', ...args);
  }
};

/* ───────────────────────── utilities ───────────────────────── */

const STATUS_LABEL = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
  new: 'New',
  scheduled: 'Scheduled',
  en_route: 'En route',
  completed: 'Completed',
  invoiced: 'Invoiced',
  planned: 'Planned',
  confirmed: 'Confirmed',
  no_show: 'No show',
};
const prettyStatus = (s) =>
  s ? STATUS_LABEL[s] || s.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : '';

const startOfDayLocal = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const startOfWeek = (d, weekStartsOn = 0) => {
  const sd = startOfDayLocal(d);
  const diff = (sd.getDay() - weekStartsOn + 7) % 7;
  return new Date(sd.getFullYear(), sd.getMonth(), sd.getDate() - diff);
};
const endOfWeek = (d, weekStartsOn = 0) => {
  const s = startOfWeek(d, weekStartsOn);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6, 23, 59, 59, 999);
};

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function inclusiveToYMDFromExclusive(endExclusive) {
  return ymdLocal(new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate() - 1));
}

// Color palette by type
const typeAccentDefaults = (type) => {
  switch (type) {
    case 'task':
      return { h: 202, s: 90, l: 45 }; // sky
    case 'job':
      return { h: 38, s: 90, l: 45 }; // amber
    default:
      return { h: 264, s: 90, l: 45 }; // violet
  }
};
const hsl = ({ h, s, l }) => `hsl(${h}deg ${s}% ${l}%)`;
const hsla = ({ h, s, l }, a) => `hsl(${h}deg ${s}% ${l}% / ${a})`;

function getEventPalette(ev) {
  const base = typeAccentDefaults(ev?.type);
  return {
    accent: hsl(base),
    bg: hsla(base, 0.12),
    border: hsla(base, 0.35),
  };
}

const PRIORITY_DOT = {
  low: 'bg-slate-300',
  normal: 'bg-slate-400',
  high: 'bg-amber-500',
  urgent: 'bg-rose-600',
};

function minutesBetween(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(0, Math.round(ms / 60000));
}

// Stable anchor date for each view to avoid visual jumps on refetch
function anchorDateForView(view, currentStart, weekStartsOn = 0) {
  if (!currentStart) return new Date();
  if (view === 'month') {
    return new Date(currentStart.getFullYear(), currentStart.getMonth(), 15, 12, 0, 0, 0);
  }
  if (view === 'week') {
    const s = startOfWeek(currentStart, weekStartsOn);
    return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 3, 12, 0, 0, 0);
  }
  return new Date(currentStart.getFullYear(), currentStart.getMonth(), currentStart.getDate(), 12, 0, 0, 0);
}

/* ───────────────────────── toolbar ───────────────────────── */

function Toolbar({
  view,
  date,
  weekStartsOn,
  density,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onDatePick,
  onToggleDensity,
}) {
  const title = useMemo(() => {
    if (view === 'month') {
      return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(date);
    }
    if (view === 'week') {
      const from = startOfWeek(date, weekStartsOn);
      const to = endOfWeek(date, weekStartsOn);
      const s = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(from);
      const e = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(to);
      return `${s} – ${e}`;
    }
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }, [view, date, weekStartsOn]);

  const inputValue = useMemo(() => ymdLocal(date), [date]);

  return (
    <div className="sticky top-0 z-10 mb-3 flex flex-wrap items-center justify-between gap-2 bg-white/90 backdrop-blur dark:bg-slate-950/90">
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          aria-label="Previous"
        >
          <FiChevronLeft />
        </button>
        <button
          onClick={onToday}
          className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
        >
          Today
        </button>
        <button
          onClick={onNext}
          className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          aria-label="Next"
        >
          <FiChevronRight />
        </button>

        <div className="ml-3 flex items-center gap-2 text-sm text-slate-800 dark:text-slate-200">
          <FiCalendar className="text-slate-500" />
          <span className="font-medium">{title}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="date"
          value={inputValue}
          onChange={(e) => onDatePick?.(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
          aria-label="Jump to date"
        />
        <div className="flex items-center gap-1">
          {VIEW_OPTS.map((v) => (
            <button
              key={v}
              onClick={() => onViewChange?.(v)}
              className={[
                'px-3 py-1.5 text-sm rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                v === view
                  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900'
                  : 'text-slate-700 border-slate-200 hover:bg-slate-50 dark:text-slate-200 dark:border-slate-800 dark:hover:bg-slate-900',
              ].join(' ')}
              aria-pressed={v === view}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <button
          onClick={onToggleDensity}
          className="ml-1 inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          title="Toggle density"
        >
          {density === 'compact' ? 'Comfortable' : 'Compact'}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────── event content renderer ─────────────────────── */

function EventContent({ event, timeText, view }) {
  const ep = event.extendedProps || {};
  const statusText = ep?.ui?.statusLabel || prettyStatus(ep?.status);
  const { accent, bg, border } = getEventPalette(ep);
  const recurrence = !!ep?.seriesRulePresent;
  const hasConflict = !!ep?.conflicts?.assigneeOverlap;
  const attachments =
    Number(ep?.attachmentsCount) > 0 ||
    Number(ep?.filesCount) > 0 ||
    (Array.isArray(ep?.attachments) && ep.attachments.length > 0);
  const clientName = ep?.clientName || ep?.client || '';
  const workNumber = ep?.workNumber || '';
  const assigneeName =
    ep?.assigneeName ||
    ep?.assignee ||
    (Array.isArray(ep?.assignees) && (ep.assignees[0]?.name || ep.assignees[0]?.displayName)) ||
    '';
  const priorityDot = PRIORITY_DOT[ep?.priority || 'normal'] || PRIORITY_DOT.normal;

  const mins = minutesBetween(event.start, event.end);
  const secondLinePieces =
    view === 'month'
      ? [clientName || null, workNumber || null, Number.isFinite(mins) && mins > 0 ? `${mins}m` : null].filter(Boolean)
      : [clientName || null, assigneeName || null, workNumber || null].filter(Boolean);

  if (CAL_DEBUG) {
    // eslint-disable-next-line no-console
    console.debug('[WorkCalendar] eventContent render', {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      view,
    });
  }

  return (
    <div
      className="relative w-full rounded-[6px] border px-2 py-1 text-[11px] leading-tight shadow-sm hover:shadow"
      style={{ background: bg, borderColor: border }}
      title={event.title}
    >
      <span className="absolute inset-y-0 left-0 w-1.5 rounded-l-[6px]" style={{ background: accent }} />
      <div className="ml-1.5 flex items-center gap-1">
        <span className="truncate font-medium text-slate-800 dark:text-slate-100">{event.title}</span>

        {statusText && (
          <span className="ml-1 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-[1px] text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">
            {statusText}
          </span>
        )}
        {recurrence && <FiRepeat className="text-[10px] text-slate-400" title="Repeats" />}
        {attachments && <FiPaperclip className="text-[10px] text-slate-400" title="Attachments" />}

        {hasConflict ? (
          <span className="ml-auto h-2 w-2 rounded-full bg-rose-500" title="Conflict" />
        ) : (
          <span className={`ml-auto h-1.5 w-1.5 rounded-full ${priorityDot}`} title={`Priority: ${ep?.priority || 'normal'}`} />
        )}
      </div>

      <div className="mt-[2px] flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-300">
        {view !== 'month' && timeText ? <span>{timeText}</span> : null}
        {secondLinePieces.length > 0 && (view !== 'month' && timeText ? <span>·</span> : null)}
        {secondLinePieces.map((t, i) => (
          <span key={i} className={i === 0 ? 'truncate' : ''}>
            {t}
            {i < secondLinePieces.length - 1 ? ' • ' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────── main ─────────────────────────────── */

export default function WorkCalendar({
  view = 'month',
  date: dateProp = new Date(),
  items = [],
  isLoading = false,
  isFetching = false,
  error,

  onRangeChange,
  onViewChange,
  onDateChange,
  onEventClick,
  onOpenDetails,

  onCreate,
  onMove,
  onResize,
  onExternalDrop,

  weekStartsOn = 0,
  timeStepMinutes = 15,
  readOnly = true,
  density: densityProp = 'comfortable',
  externalMimeType = 'application/x-hypercrm-work',
  defaultDropDurationMinutes = 60,
}) {
  const calendarRef = useRef(null);
  const containerRef = useRef(null);
  const initialDateRef = useRef(dateProp || new Date());
  const lastSentRangeRef = useRef({ from: '', to: '' });
  const [toolbarDate, setToolbarDate] = useState(() => initialDateRef.current);
  const [density, setDensity] = useState(densityProp);

  // 🧩 NEW: responsive width cap per-view so the grid never stretches too wide
  const maxWidth = view === 'month' ? 1400 : view === 'week' ? 1220 : 1000;

  // Initial mount log
  useEffect(() => {
    calLog('mount', { initialView: view, initialDate: initialDateRef.current });
    return () => calLog('unmount');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prop/state change logs
  useEffect(() => {
    calLog('props/items changed', { count: Array.isArray(items) ? items.length : 0 });
  }, [items]);
  useEffect(() => {
    calLog('props/status', { isLoading, isFetching, error: error?.message });
  }, [isLoading, isFetching, error]);
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      calLog('container size', { width: rect.width, height: rect.height });
    }
  });

  // Derived config for compact vs comfortable
  const isCompact = density === 'compact';
  const slotMinTime = isCompact ? '08:00:00' : '06:00:00';
  const slotMaxTime = isCompact ? '19:00:00' : '21:00:00';
  const scrollTime = isCompact ? '08:00:00' : '07:00:00';
  const slotDuration = isCompact ? { minutes: 30 } : { minutes: timeStepMinutes };
  const slotLabelInterval = isCompact ? { hours: 1 } : undefined;
  const showAllDay = !isCompact;
  const contentHeight = isCompact ? 640 : 820;
  const displayEventEnd = !isCompact;

  // Map normalized items → FullCalendar events (per-occurrence id)
  const events = useMemo(() => {
    const out = (items || []).map((ev, i) => ({
      id: String(ev.id ?? ev.recurrenceInstanceKey ?? `${ev.workId || 'work'}:${ev.start || i}`),
      title: ev.title || ev.summary || ev.name || ev.work?.title || '(no title)',
      start: ev.start,
      end: ev.end,
      allDay: !!ev.allDay,
      extendedProps: ev,
    }));
    calLog('events built', { count: out.length, sample: out.slice(0, 3) });
    return out;
  }, [items]);

  // Keep FullCalendar in sync when parent changes *view* (but NOT date)
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const fcView = view === 'month' ? 'dayGridMonth' : view === 'week' ? 'timeGridWeek' : 'timeGridDay';
    if (api.view.type !== fcView) {
      calLog('changeView', { from: api.view.type, to: fcView });
      api.changeView(fcView);
    }
  }, [view]);

  // Emit LOCAL range + anchor date on every view/date change
  const handleDatesSet = useCallback(
    (arg) => {
      const start = arg.start;
      const endExclusive = arg.end;

      const from = ymdLocal(start);
      const to = inclusiveToYMDFromExclusive(endExclusive);

      const last = lastSentRangeRef.current;
      if (from !== last.from || to !== last.to) {
        lastSentRangeRef.current = { from, to };
        calLog('datesSet: emit range', { from, to });
        onRangeChange?.({ from, to });
      } else {
        calLog('datesSet: range unchanged', { from, to });
      }

      const type = arg.view?.type;
      const v = type === 'dayGridMonth' ? 'month' : type === 'timeGridWeek' ? 'week' : 'day';
      calLog('datesSet: emit view', { v, fcType: type });
      onViewChange?.(v);

      const anchor = anchorDateForView(v, arg.view?.currentStart || arg.start, weekStartsOn);
      setToolbarDate(anchor);
      onDateChange?.(anchor);
      calLog('datesSet: anchor', { anchor });
    },
    [onRangeChange, onViewChange, onDateChange, weekStartsOn]
  );

  // create via select
  const handleSelect = useCallback(
    (sel) => {
      calLog('select', { start: sel.start, end: sel.end, allDay: sel.allDay, readOnly });
      if (readOnly) return;
      onCreate?.({ start: sel.start, end: sel.end, allDay: sel.allDay });
    },
    [onCreate, readOnly]
  );

  // click
  const handleEventClick = useCallback(
    (click) => {
      const workId = String(click.event.extendedProps?.workId || click.event.id);
      calLog('eventClick', { workId });
      (onEventClick || onOpenDetails)?.(workId);
    },
    [onEventClick, onOpenDetails]
  );

  // Avoid editing recurring parent series for now
  const isSeries = (ep) => ep?.seriesRulePresent === true;

  // drag-move existing event
  const handleEventDrop = useCallback(
    (info) => {
      const ep = info.event.extendedProps || {};
      calLog('eventDrop', { id: info.event.id, readOnly, isSeries: isSeries(ep) });
      if (readOnly || isSeries(ep)) {
        info.revert();
        return;
      }
      onMove?.({
        id: String(ep.workId || info.event.id),
        start: info.event.start,
        end: info.event.end || info.event.start,
        allDay: info.event.allDay,
      });
    },
    [onMove, readOnly]
  );

  // resize
  const handleEventResize = useCallback(
    (info) => {
      const ep = info.event.extendedProps || {};
      calLog('eventResize', { id: info.event.id, readOnly, isSeries: isSeries(ep) });
      if (readOnly || isSeries(ep)) {
        info.revert();
        return;
      }
      onResize?.({
        id: String(ep.workId || info.event.id),
        start: info.event.start,
        end: info.event.end || info.event.start,
        allDay: info.event.allDay,
      });
    },
    [onResize, readOnly]
  );

  // external drop from UnscheduledTray
  const handleExternalDrop = useCallback(
    (info) => {
      if (readOnly) return;
      let payload = null;
      try {
        const dt = info?.jsEvent && 'dataTransfer' in info.jsEvent ? info.jsEvent.dataTransfer : null;
        const raw = dt?.getData?.(externalMimeType) || dt?.getData?.('application/json') || '';
        if (raw) payload = JSON.parse(raw);
      } catch {
        payload = null;
      }

      if (!payload) {
        const el = info?.draggedEl;
        const fromDataset = el && (el.dataset?.workId || el.getAttribute?.('data-work-id'));
        if (fromDataset) {
          payload = {
            id: String(fromDataset),
            type: el.dataset?.workType,
            title: el.dataset?.workTitle,
            defaultDurationMinutes: Number(el.dataset?.defaultDurationMinutes) || undefined,
          };
        }
      }

      if (!payload || !payload.id) return;

      const start = info.date;
      const viewType = info?.view?.type || '';
      const isMonth = viewType === 'dayGridMonth';
      const allDay = Boolean(isMonth || info.allDay);

      let end;
      if (allDay) {
        end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
      } else {
        const mins =
          Number.isFinite(payload.defaultDurationMinutes) ? payload.defaultDurationMinutes : defaultDropDurationMinutes;
        end = new Date(start.getTime() + Math.max(15, mins) * 60 * 1000);
      }

      const fn = typeof onExternalDrop === 'function' ? onExternalDrop : onMove;
      calLog('externalDrop', { payload, start, end, allDay });
      fn?.({ id: String(payload.id), start, end, allDay, source: 'external-drop' });
    },
    [readOnly, externalMimeType, defaultDropDurationMinutes, onExternalDrop, onMove]
  );

  // toolbar actions
  const goPrev = useCallback(() => {
    calLog('action:prev');
    calendarRef.current?.getApi().prev();
  }, []);
  const goNext = useCallback(() => {
    calLog('action:next');
    calendarRef.current?.getApi().next();
  }, []);
  const goToday = useCallback(() => {
    calLog('action:today');
    calendarRef.current?.getApi().today();
  }, []);
  const onDatePick = useCallback((value) => {
    calLog('action:gotoDate', { value });
    if (!value) return;
    const [y, m, d] = value.split('-').map((x) => parseInt(x, 10));
    calendarRef.current?.getApi().gotoDate(new Date(y, m - 1, d));
  }, []);
  const onViewBtn = useCallback((v) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const type = v === 'month' ? 'dayGridMonth' : v === 'week' ? 'timeGridWeek' : 'timeGridDay';
    calLog('action:changeView', { v, type });
    api.changeView(type);
  }, []);

  return (
    <div className="relative">
      {isFetching && !isLoading && (
        <div className="absolute inset-x-0 -top-1 h-0.5 animate-pulse rounded bg-gradient-to-r from-transparent via-indigo-400 to-transparent" />
      )}

      {/* Width-capped shell so calendar never stretches on large screens / zoom-out */}
      <div className="mx-auto w-full px-1 sm:px-2" style={{ maxWidth }}>
        <Toolbar
          view={view}
          date={toolbarDate}
          weekStartsOn={weekStartsOn}
          density={density}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
          onViewChange={onViewBtn}
          onDatePick={onDatePick}
          onToggleDensity={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}
        />

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-950/40 dark:bg-rose-950/20 dark:text-rose-200">
            {String(error?.message || 'Failed to load calendar')}
          </div>
        ) : (
          <div
            ref={containerRef}
            className="rounded-xl border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-950"
            style={{ minHeight: 560 }} // guard against zero-height containers
          >
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={false}
              initialView={view === 'month' ? 'dayGridMonth' : view === 'week' ? 'timeGridWeek' : 'timeGridDay'}
              initialDate={initialDateRef.current}
              firstDay={weekStartsOn}
              slotDuration={slotDuration}
              slotLabelInterval={slotLabelInterval}
              slotMinTime={slotMinTime}
              slotMaxTime={slotMaxTime}
              scrollTime={scrollTime}
              allDaySlot={showAllDay}
              businessHours={{ startTime: slotMinTime, endTime: slotMaxTime }}
              contentHeight={contentHeight}
              expandRows={false}
              dayMaxEvents
              nowIndicator
              selectable={!readOnly}
              selectMirror
              editable={!readOnly}
              droppable={!readOnly}
              dropAccept={() => true}
              eventOverlap
              events={events}
              eventsSet={(evs) => calLog('eventsSet', { count: evs.length, sample: evs.slice(0, 3) })}
              eventDidMount={(info) =>
                calLog('eventDidMount', {
                  id: info?.event?.id,
                  title: info?.event?.title,
                  start: info?.event?.start,
                  end: info?.event?.end,
                })
              }
              eventContent={(arg) => <EventContent {...arg} view={view} />}
              datesSet={handleDatesSet}
              select={handleSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              drop={handleExternalDrop}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                omitZeroMinute: false,
                meridiem: 'short',
              }}
              moreLinkClick="popover"
              displayEventTime
              displayEventEnd={displayEventEnd}
            />
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-sky-500" /> Task
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Job
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-violet-500" /> Appointment
          </div>
          <div className="ml-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-rose-500" /> Conflict
          </div>
        </div>
      </div>
    </div>
  );
}
